import type { RemoteExtensionsConfiguration } from 'src/gql/graphql';
import PublicEnv from 'shared/utils/public-env';

const LIVE_PREVIEW_SCOPE = 'customer_themes';

// Hosts allowed for manifest_url in production; anything else is rejected
// to prevent loading arbitrary remote code via this parameter.
const PRODUCTION_MANIFEST_HOST_ALLOWLIST: RegExp[] = [/^localhost$/, /^127\.0\.0\.1$/, /(^|\.)dexus\.io$/];

function isAllowedProductionManifestHost(hostname: string): boolean {
  return PRODUCTION_MANIFEST_HOST_ALLOWLIST.some((pattern) => pattern.test(hostname));
}

/**
 * Reads `manifest_url` and `theme` query params to build a live preview config for module federation.
 *
 * In production, `manifest_url` is restricted to localhost and `*.dexus.io` hosts
 * to prevent executing attacker-controlled JS in the authenticated production
 * page context. Non-production environments allow any http(s) host.
 */
export function getLivePreviewConfig(): RemoteExtensionsConfiguration | null {
  if (typeof window === 'undefined') return null;

  const params = new URLSearchParams(window.location.search);
  const manifestUrl = params.get('manifest_url');
  const theme = params.get('theme');

  if (!manifestUrl || !theme) return null;

  // Require a well-formed http(s) URL so a javascript:/data: URL isn't passed
  // to Module Federation.
  try {
    const url = new URL(manifestUrl);
    if (!['http:', 'https:'].includes(url.protocol)) return null;

    if (PublicEnv.appEnv === 'production' && !isAllowedProductionManifestHost(url.hostname)) {
      return null;
    }
  } catch {
    return null;
  }

  return {
    manifestUrl,
    modulePath: `themes/${theme}`,
    scope: LIVE_PREVIEW_SCOPE,
  };
}
